import { createContext, useContext, useEffect } from 'react';

/**
 * What a record in the trail is CALLED.
 *
 * The breadcrumb shortened every id segment to `d37ba0fb…`, on the page whose
 * own header says "Morgan". A trail is for orientation, and a truncated UUID
 * orients nobody — it is the `policyLabel` lesson ("nobody can retune a UUID")
 * one component up.
 *
 * The PROVIDER lives in `BreadcrumbNameProvider.tsx` — a file exporting both a
 * component and hooks defeats fast refresh, which is what
 * `react-refresh/only-export-components` is warning about.
 *
 * ⚠️ **The PAGE publishes the name; the breadcrumb never fetches it.** The
 * detail page already holds the record, so a lookup here would be a second
 * request for something the application has in hand — and on a 404 it would be
 * a second request that fails, producing a spinner in the trail for a page
 * already showing an error. This is the same argument `Can` and `useFeatures`
 * make for asking rather than re-deriving.
 *
 * Keyed by the SEGMENT VALUE — the id itself — rather than by path. Moving from
 * one partner to another changes the key, so a name can never survive onto a
 * different record; a path key would be identical for `/platform/white-labels/A`
 * and `.../B` at the moment the second mounts, and the trail would show A's name
 * over B's page for one frame.
 */
export type Names = Readonly<Record<string, string>>;

export interface Store {
  names: Names;
  publish: (id: string, name: string) => void;
  withdraw: (id: string) => void;
}

export const BreadcrumbNameContext = createContext<Store | null>(null);

/** The published names, or an empty map outside a provider. */
export function useBreadcrumbNames(): Names {
  return useContext(BreadcrumbNameContext)?.names ?? {};
}

/**
 * Tell the trail what this record is called.
 *
 * ```ts
 * useBreadcrumbName(id, partner.data?.displayName);
 * ```
 *
 * Three rules, each a wrong answer inverted:
 *
 *  - **`undefined` publishes NOTHING**, so the trail keeps showing the shortened
 *    id while the record loads. That is honest — the id is what the URL says —
 *    where a placeholder like "Loading…" would stick permanently on any page
 *    whose fetch fails, and a blank crumb would read as a rendering fault.
 *  - **An EMPTY name is ignored too.** Some records legitimately have none, and
 *    a blank final crumb is worse than a short id.
 *  - **Withdrawn on unmount**, so a name never outlives the page that knew it.
 *    Without this, deleting a record and returning to the list would leave its
 *    name in the map, ready to label a different page that happened to reuse
 *    the id.
 */
export function useBreadcrumbName(id: string | undefined, name: string | null | undefined): void {
  const store = useContext(BreadcrumbNameContext);

  useEffect(() => {
    if (!store || !id) return;
    const trimmed = typeof name === 'string' ? name.trim() : '';
    if (trimmed === '') return;
    store.publish(id, trimmed);
    return () => store.withdraw(id);
    // `store` is recreated whenever the map changes; depending on it would
    // withdraw and republish on every publish. The two values below are what
    // actually decide what is published.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, name]);
}
